<script setup lang="ts">
import { ref, watch } from 'vue';
import moment from 'moment';
import {
  Header,
  CustomButton,
  TableIconText,
  UserAvatar,
  Table,
  ButtonType,
  NewExportDialog,
  ExportDeleteAlertDialog
} from '@/components';
import { TableColumnType } from '../common/table/interfaces/TableColumnModel';
import { ExportModel } from '@/models';
import { useExportStore } from '@/stores/export';
import { useStudyStore } from '@/stores/study';

const showExportsDeleteAlert = ref<boolean>(false);
const selectedRows = ref<ExportModel[]>([]);

const exportStore = useExportStore();
exportStore.getStudyExports();

const studyStore = useStudyStore();
watch(
  () => studyStore.selectedStudy,
  (newId, oldId) => {
    if (oldId && newId !== oldId) {
      exportStore.getStudyExports();
    }
  }
);

const downloadExport = async (id: string) => {
  const exportLink = await exportStore.getExportLink(id);

  if (exportLink) {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = exportLink.presignedUrl;

    document.body.appendChild(iframe);
    iframe.onload = () => {
      document.body.removeChild(iframe);
    };
  }
};

const formattedDate = (date: Date) => {
  return moment(date).format('MMM D, YYYY [at] h:mm A');
};

const openExportDialog = ref(false);

const openNewExportDialog = () => {
  openExportDialog.value = !openExportDialog.value;
};

const openCloseDeleteAlert = () => {
  showExportsDeleteAlert.value = !showExportsDeleteAlert.value;
};

const deleteExports = async () => {
  await exportStore.deleteExports(selectedRows.value);
  selectedRows.value = [];
  openCloseDeleteAlert();
  exportStore.getStudyExports();
};

const retryExport = async (id: string) => {
  await exportStore.retryExport(id);
};

</script>

<template>
  <div class="export">
    <div class="exportWrapper">
      <div class="header">
        <Header title="Data Exports">
          <template #headerButtons>
              <div class="header-buttons">
                <CustomButton text="New data export"
                  v-if="selectedRows.length == 0"
                  :type="ButtonType.POSITIVE"
                  :icon="{ icon: 'download', style: '' }"
                  @onClick="openNewExportDialog"/>
                <CustomButton text="Delete"
                  :icon="{ style: 'fas', icon: 'trash' }"
                  v-if="selectedRows.length > 0"
                  :type="ButtonType.NEGATIVE"
                  @onClick="openCloseDeleteAlert"/>
              </div>
          </template>
        </Header>
      </div>
      <div class="table">
        <Table :columns="exportStore.tableColumns"
          :data-list="exportStore.exportList"
          :loading-state="exportStore.isExportLoading.isLoading"
          :selection="true"
          v-model:selectedRows="selectedRows"
          presort="createdAt">
          <template #bodyContent="{ data, col }">
            <UserAvatar :avatarUrl="data.avatar"
              :userName="data[col.key]"
              :size="28"
              v-if="col.type === TableColumnType.USER"/>

            <TableIconText :text="formattedDate(data[col.key])"
              :icon="col.icon"
              v-if="col.type === TableColumnType.TEXT_WTIH_ICON"/>

            <CustomButton :text="col.key"
              :icon="col.icon"
              v-if="
                col.type === TableColumnType.BUTTON_WITH_ICON &&
                (data.storedAt || data.preparing)
              "
              :loading="data.preparing"
              :type="ButtonType.OUTLINED"
              @onClick="downloadExport(data.identifier)"/>

            <div v-if="
                col.type === TableColumnType.BUTTON_WITH_ICON &&
                !data.storedAt &&
                !data.preparing &&
                !data.error
              ">
              We're building your export
            </div>

            <div v-if="
              col.type === TableColumnType.BUTTON_WITH_ICON &&
              !data.storedAt &&
              !data.preparing &&
              data.error"
              class="retry-content">
              <CustomButton text="Re-try"
                :icon="{
                  icon: 'circle-exclamation',
                  style: 'far'
                }"
                :loading="data.preparing"
                :type="ButtonType.OUTLINED"
                v-tooltip="data.error.message"
                @onClick="retryExport(data.identifier)"/>
              <a href="mailto:support@yuzulabs.com">
                <FontAwesomeIcon :icon="['far', 'headset']" color="black" v-tooltip="'Support'"/>
              </a>
            </div>

            <div v-if="col.exportFormNames && col.type === TableColumnType.TEXT">
              {{ data[col.exportFormNames] }}
            </div>

            <div v-if="col.type === TableColumnType.TEXT">
              {{ data[col.key] }}
            </div>
          </template>
          <template #emptyContent>
            <div class="empty-content-container">
              <div class="empty-content">
                <FontAwesomeIcon :icon="[`fal`, 'download']" class="download-icon" />
                <h3 class="no-entries-label">No exports created yet</h3>
                <div class="create-context-wrapper">
                  <div class="new-entry-text">To create an export click on</div>
                  <CustomButton text="New data export"
                    :type="ButtonType.POSITIVE"
                    :icon="{ icon: 'download', style: '' }"
                    :marginLeft="16"
                    @onClick="openNewExportDialog"/>
                </div>
              </div>
            </div>
          </template>
        </Table>
      </div>
    </div>
  </div>
  <ExportDeleteAlertDialog v-if="showExportsDeleteAlert"
    :visible="showExportsDeleteAlert"
    :isLoading="exportStore.deleteExportsLoader.isLoading"
    @onClose="openCloseDeleteAlert"
    @onDelete="deleteExports"/>

  <NewExportDialog :visible="openExportDialog" @onClose="openNewExportDialog" />
</template>

<style scoped lang="scss">
.export {
  padding: 48px 100px 40px 80px;
  height: 100%;
}
.exportWrapper {
  max-width: 1100px;
  height: 100%;
  display: flex;
  flex-direction: column;
}
.table {
  flex: 1;
  overflow: hidden;
  box-shadow: 0 0 0 1px #ebeff2, 0 1px 10px 1px rgba(15, 15, 15, 0.05),
    0 1px 3px 1px rgba(15, 15, 15, 0.05);
}
.create-context-wrapper {
  display: flex;
  align-items: center;
  margin: 16px 0;
}
.empty-content-container {
  padding: 50px 0px;
  display: flex;
  justify-content: center;
  align-items: center;
  .empty-content {
    text-align: center;
    .download-icon {
      font-size: 100px;
      margin: 16px 0px;
    }

    .no-entries-label {
      font-weight: bold;
    }
  }
}
.retry-content {
  display: flex;
  align-items: center;
  gap: 12px;
}
</style>
